/**
 * Stock Planning filter panel.
 *
 * Restyled onto the app-wide sectioned-filter primitives
 * (`components/filters/FilterSection`) so this panel groups and labels its
 * fields the same way as Parts Master, the dashboard and the Purchasing tabs.
 * Presentation only — every control kind, prop, handler and value binding is
 * unchanged: the supplier picker is still a native combobox input over a custom
 * dropdown, and Commodity Type is still a native <select>.
 */
import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { callOffAPI, purchasingAPI, fetchAllPages } from '../../../lib/api';
import { Card, CardContent } from '../../ui/card';
import {
  FilterSection,
  FilterGrid,
  FilterField,
  filterControlClass,
} from '../../filters/FilterSection';
import { Input } from '../../ui/input';

interface FiltersState {
  supplierId: string;
  openPoDays: number;
  salCommittedEndDate: string;
  factoryFilter: string;
  commodityType: string;
}

interface StockPlanningFiltersProps {
  filters: FiltersState;
  onFiltersChange: (filters: FiltersState) => void;
}

export default function StockPlanningFilters({ filters, onFiltersChange }: StockPlanningFiltersProps) {
  const [suppliers, setSuppliers] = useState<{ id: number; name: string; supplier_code: string }[]>([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [commodityTypes, setCommodityTypes] = useState<string[]>([]);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const res = await callOffAPI.getSuppliers();
        setSuppliers(res.data);
      } catch {
        // Silently fail — suppliers are optional filters
      }
    };
    loadSuppliers();
  }, []);

  useEffect(() => {
    const loadCommodityTypes = async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const list = await fetchAllPages<{ name: string }>(
          (page) => purchasingAPI.getCommodityTypes({ enabled: true, page_size: 100, page }),
        );
        const names = Array.from(new Set(list.map(ct => ct.name).filter(Boolean)));
        names.sort((a, b) => a.localeCompare(b));
        setCommodityTypes(names);
      } catch {
        // Silently fail — commodity type is an optional filter
      }
    };
    loadCommodityTypes();
  }, []);

  const filteredSuppliers = useMemo(() => {
    const term = supplierSearch.toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(term));
  }, [suppliers, supplierSearch]);

  const selectedSupplierName = useMemo(() => {
    if (!filters.supplierId) return '';
    const s = suppliers.find(s => String(s.id) === filters.supplierId);
    return s?.name || '';
  }, [filters.supplierId, suppliers]);

  return (
    <Card>
      <CardContent className="pt-6">
        <FilterSection title="Planning Filters" icon={SlidersHorizontal} bare>
          <FilterGrid>
            <FilterField label="Supplier">
              {/* Anchor for the results dropdown and the clear button. */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={filters.supplierId ? selectedSupplierName : supplierSearch}
                  onChange={(e) => {
                    setSupplierSearch(e.target.value);
                    onFiltersChange({ ...filters, supplierId: '' });
                    setSupplierDropdownOpen(true);
                  }}
                  onFocus={() => setSupplierDropdownOpen(true)}
                  className={`${filterControlClass} pr-8`}
                />
                <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                {filters.supplierId && (
                  <button
                    type="button"
                    onClick={() => {
                      onFiltersChange({ ...filters, supplierId: '' });
                      setSupplierSearch('');
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                {supplierDropdownOpen && filteredSuppliers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-auto">
                    {filteredSuppliers.slice(0, 50).map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          onFiltersChange({ ...filters, supplierId: String(s.id) });
                          setSupplierSearch('');
                          setSupplierDropdownOpen(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
                {supplierDropdownOpen && (
                  <div className="fixed inset-0 z-40" onClick={() => setSupplierDropdownOpen(false)} />
                )}
              </div>
            </FilterField>

            <FilterField label="Open PO Days">
              <Input
                type="number"
                value={filters.openPoDays}
                onChange={(e) => onFiltersChange({ ...filters, openPoDays: parseInt(e.target.value) || 7 })}
              />
            </FilterField>

            <FilterField label="SAL Committed End Date">
              <Input
                type="date"
                value={filters.salCommittedEndDate}
                onChange={(e) => onFiltersChange({ ...filters, salCommittedEndDate: e.target.value })}
              />
            </FilterField>

            <FilterField label="Commodity Type">
              <select
                value={filters.commodityType || 'all'}
                onChange={(e) => onFiltersChange({
                  ...filters,
                  commodityType: e.target.value === 'all' ? '' : e.target.value,
                })}
                className={filterControlClass}
                aria-label="Commodity type filter"
              >
                <option value="all">All commodity types</option>
                {commodityTypes.map(ct => (
                  <option key={ct} value={ct}>{ct}</option>
                ))}
              </select>
            </FilterField>
          </FilterGrid>
        </FilterSection>
      </CardContent>
    </Card>
  );
}
